import type { ActiveOrg } from './activeOrg';

/**
 * The Clerk permissions the app gates on, spelled exactly as Clerk does.
 *
 * System permissions rather than custom ones, because of how the first Organizations release is cut
 * (MDC-3792): two roles, where every member may create, edit and delete the organization's records
 * and only the owner may manage its members, billing and settings. No record action is gated at all,
 * so what is left is exactly what Clerk's system permissions describe.
 *
 * A permission rather than a role slug, because the owner's role is not called the same thing on both
 * instances — `org:owner` on development, `org:admin` on production — while both hold these and
 * `org:member` holds none of them. A slug Clerk does not know answers `false` forever and looks exactly
 * like a member, so check both instances before adding one here.
 *
 * Billing belongs to the organization since the backend moved Stripe to it (#1239), and it is the
 * owner's alone: the `/subscription/*` routes refuse anyone who isn't the organization's owner.
 */
export const ORG_PERMISSION = {
  /** Invite and remove members. What makes someone the organization's owner in this release. */
  manageMembers: 'org:sys_memberships:manage',
  /** Rename the organization. The owner's alone, like `manageMembers`. */
  manageProfile: 'org:sys_profile:manage',
  /** Change plan, check out, cancel, see invoices and the card on file. The owner's alone. */
  manageBilling: 'org:sys_billing:manage',
} as const;

export type OrgPermission = (typeof ORG_PERMISSION)[keyof typeof ORG_PERMISSION];

/**
 * `useAuth().has`, narrowed to the one question asked of it here, and undefined until Clerk loads.
 *
 * Declared loosely for the same reason as `SessionClaimsLike`: Clerk's own type comes from a
 * different package on each platform, and this one is consumed by apps with no Clerk at all.
 *
 * Client-side, Clerk answers from the active membership's full permission list, system permissions
 * included — unlike the session token, which carries custom permissions only. So these checks are
 * only meaningful in the browser and the app, never against a token on the server.
 */
export type HasOrgPermission = ((params: { permission: string }) => boolean) | undefined;

/**
 * Whether the membership the app is acting in holds `permission`.
 *
 * Fails closed whenever the answer is not Clerk's own, live one:
 *
 * - **Clerk has not loaded.** `has` is undefined on the first render, and an owner seeing the member's
 *   UI for a frame is better than a member seeing the owner's.
 * - **The org came from storage.** On a cold offline boot the stored org is all there is, but it says
 *   nothing about the role held in it now, and everything gated here needs the network anyway.
 * - **There is no active org.** No membership, nothing to hold a permission in.
 *
 * This is UX, never security. The backend refuses what a role may not do; this hides it.
 */
export const checkOrgPermission = (activeOrg: ActiveOrg, has: HasOrgPermission, permission: OrgPermission): boolean => {
  if (!activeOrg.isLoaded || activeOrg.source !== 'session' || !has) {
    return false;
  }

  return has({ permission });
};
